import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { Alert, Button, Card, Field, Input, PasswordInput } from '../components/ui';
import { useT } from '../i18n/locale';
import { useSession } from '../lib/session';
import { ROLE_LABEL, UserRole } from '../api/enums';

/**
 * The signed-in person's own account.
 *
 * The change-password endpoint has existed since the beginning with nothing
 * calling it, so anyone whose password was set for them by an admin had no
 * way to replace it. Available to every role, because every role has a
 * password.
 */
export default function AccountScreen() {
  const t = useT();
  const { user, tenant, refresh } = useSession();

  const isOwner = user?.role === UserRole.Owner;

  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Owner-only profile editing. The owner has no admin above them to set their
  // name and email, so they do it here; everyone else keeps the read-only view.
  const [editing, setEditing] = useState(false);
  const [profName, setProfName] = useState('');
  const [profEmail, setProfEmail] = useState('');
  const [profBusy, setProfBusy] = useState(false);
  const [profError, setProfError] = useState<string | null>(null);
  const [profDone, setProfDone] = useState(false);

  function startEdit() {
    setProfName(user?.name ?? '');
    setProfEmail(user?.email ?? '');
    setProfError(null);
    setProfDone(false);
    setEditing(true);
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setProfBusy(true);
    setProfError(null);
    try {
      await api.patch('/account/profile', { name: profName.trim(), email: profEmail.trim() });
      await refresh();
      setEditing(false);
      setProfDone(true);
    } catch (err) {
      setProfError(err instanceof ApiError ? err.message : t('account.profileFailed'));
    } finally {
      setProfBusy(false);
    }
  }

  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const same = newPassword.length > 0 && newPassword === currentPassword;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.post('/change-password', { currentPassword, newPassword });
      setDone(true);
      // Clear it: a password left sitting in a form is one a passer-by can
      // read off an unattended warehouse terminal.
      setCurrent('');
      setNew('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('account.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md p-4">
      <Card className="mb-3 p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="m-0 text-base font-bold text-slate-800">{t('account.myAccount')}</h2>
          {isOwner && !editing ? (
            <Button variant="ghost" onClick={startEdit}>
              {t('account.editProfile')}
            </Button>
          ) : null}
        </div>

        {profDone ? (
          <div className="mb-3">
            <Alert tone="success">{t('account.profileSaved')}</Alert>
          </div>
        ) : null}

        {editing ? (
          <form onSubmit={saveProfile} className="flex flex-col gap-3">
            {profError ? <Alert tone="error">{profError}</Alert> : null}
            <Field label={t('account.name')}>
              <Input value={profName} onChange={(e) => setProfName(e.target.value)} autoFocus />
            </Field>
            <Field label={t('account.email')}>
              <Input
                type="email"
                value={profEmail}
                onChange={(e) => setProfEmail(e.target.value)}
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={() => setEditing(false)} disabled={profBusy}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={profBusy || profName.trim().length < 2 || !profEmail.trim()}
              >
                {profBusy ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-slate-500">{t('account.name')}</dt>
              <dd className="m-0 font-semibold text-slate-800">{user?.name ?? '-'}</dd>
              <dt className="text-slate-500">{t('account.email')}</dt>
              <dd className="m-0 break-all text-slate-800">{user?.email ?? '-'}</dd>
              <dt className="text-slate-500">{t('account.role')}</dt>
              <dd className="m-0 text-slate-800">{user ? t(ROLE_LABEL[user.role]) : '-'}</dd>
              <dt className="text-slate-500">{t('account.organisation')}</dt>
              <dd className="m-0 text-slate-800">{tenant?.name ?? '-'}</dd>
            </dl>
            {!isOwner ? (
              <p className="m-0 mt-3 text-xs text-slate-500">{t('account.managedByAdmin')}</p>
            ) : null}
          </>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="m-0 mb-3 text-base font-bold text-slate-800">{t('account.changePassword')}</h3>
        {done ? <Alert tone="success">{t('account.changed')}</Alert> : null}

        <form onSubmit={submit} className="flex flex-col gap-3">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <Field label={t('account.current')}>
            <PasswordInput
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <Field
            label={t('account.new')}
            error={same ? t('account.mustDiffer') : undefined}
          >
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field label={t('account.repeat')} error={mismatch ? t('account.mismatch') : undefined}>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>

          <Button
            type="submit"
            disabled={busy || !currentPassword || !newPassword || !confirm || mismatch || same}
          >
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
